/**
 * @name                                    format.ts
 * @description                             This module provides functions to format text with ANSI styles, padding, and borders.
*/


/* ---------------------------------------- PACK ----------------------------------------  */

    import * as ansi                        from '@je-es/ansi';
    import * as types                       from './types';

/* ---------------------------------------- ---- ----------------------------------------  */


/* ---------------------------------------- CORE ----------------------------------------  */

    /**
     * Applies styles, padding, and borders to a given string.
     *
     * @param {string} input - The string to apply styles to.
     * @param {types.formatOptions} options - The formatting options.
     * @param {types.t_style} options.ansi - The ANSI styles to apply.
     * @param {types.t_prefix} options.prefix - The prefix options.
     * @param {types.t_prefix} options.suffix - The suffix options.
     * @param {number} options.width - The width of the formatted string.
     * @param {'left'|'center'|'right'} options.align - The alignment of the formatted string.
     * @param {types.t_padding} options.padding - The padding options.
     * @param {types.t_border} options.border - The border options.
     * @return {string} The formatted string.
     */
    export const style
    = (input: string, options: types.formatOptions = {})
    : string =>
    {
        if (Object.keys(options).length === 0) return input;

        let     res             = '';
        let     originalLength  = input.length + (options.prefix?.value?.length || 0) + (options.suffix?.value?.length || 0);

        // Apply Prefix
        if(options.prefix)
        {

            if(options.ansi?.bg && !(options.prefix?.style?.bg))
            options.prefix.style.bg = options.ansi?.bg;

            if(options.ansi?.fg && !(options.prefix?.style?.fg))
            options.prefix.style.fg = options.ansi?.fg;

            if(options.ansi?.attr && !(options.prefix?.style?.attr))
            options.prefix.style.attr = options.ansi?.attr;

            res += Helpers.ApplyPrefix(options.prefix);
        }

        // Apply ANSI styles
        if (options.ansi)       res += Helpers.ApplyStyle(input, options);

        // Apply Suffix
        if(options.suffix)
        {

            if(options.ansi?.bg && !(options.suffix?.style?.bg))
            options.suffix.style.bg = options.ansi?.bg;

            if(options.ansi?.fg && !(options.suffix?.style?.fg))
            options.suffix.style.fg = options.ansi?.fg;

            if(options.ansi?.attr && !(options.suffix?.style?.attr))
            options.suffix.style.attr = options.ansi?.attr;

            res += Helpers.ApplySuffix(options.suffix);
        }

        // Apply Width
        if (options.width && options.width > originalLength)
        {
            // Apply Alignment
            {
                if(options.align === 'center')
                {
                    const left = Math.floor((options.width - originalLength) / 2);
                    const right = options.width - originalLength - left;
                    res = ' '.repeat(left) + res + ' '.repeat(right);
                }
                else if(options.align === 'right')
                {
                    const left = options.width - originalLength;
                    res = ' '.repeat(left) + res;
                }
                else
                {
                    const right = options.width - originalLength;
                    res += ' '.repeat(right);
                }
            }

            // Save original length
            originalLength = options.width;
        }

        // Apply padding
        if (options.padding)    res = Helpers.ApplyPadding(res, options);

        // Apply border
        if (options.border)     res = Helpers.ApplyBorder(res, options, originalLength);

        return res;
    };

/* ---------------------------------------- ---- ----------------------------------------  */


/* ---------------------------------------- HELP ----------------------------------------  */

    const Helpers =
    {
        ApplyPrefix
        : (options: types.t_prefix)
        : string =>
        {
            const { value, style } = options;
            return ansi.style(value, style);
        },

        ApplySuffix
        : (options: types.t_prefix)
        : string =>
        {
            const { value, style } = options;
            return ansi.style(value, style);
        },

        ApplyStyle
        : (input: string, options: types.formatOptions)
        : string =>
        {
            const { fg, bg, attr } = options.ansi || {};
            return ansi.style(input, { fg, bg, attr });
        },

        ApplyPadding
        : (input: string, options: types.formatOptions)
        : string =>
        {
            if (options.padding?.left)
            {
                input = ' '.repeat(options.padding.left) + input;
            }

            if (options.padding?.right)
            {
                input = input + ' '.repeat(options.padding.right);
            }

            if (options.padding?.top)
            {
                input = '\n'.repeat(options.padding.top) + input;
            }

            if (options.padding?.bottom)
            {
                input = input + '\n'.repeat(options.padding.bottom);
            }

            return input;
        },

        ApplyBorder
        : (input: string, options: types.formatOptions, originalLength: number)
        : string =>
        {
            // options.border = { char, color, width }

            const   Bwidth              : number = options.border?.width || 1;
            const   color               : string = options.border?.color || 'white';
            const   _paddingWidth       : number = Helpers.More.__GetPaddingWidth(options);
            const   width               : number = Math.max(originalLength + _paddingWidth + 2, 0);
            let     chars               : types.borderDimensions = { topBeg: '', topEnd: '', botBeg: '', botEnd: '', char: '', side: '' };

            switch (Bwidth)
            {
                case 1:
                    chars = { topBeg: '┌', topEnd: '┐', botBeg: '└', botEnd: '┘', char: '─', side: '│' };
                    break;
                case 2:
                    chars = { topBeg: '╔', topEnd: '╗', botBeg: '╚', botEnd: '╝', char: '═', side: '║' };
                    break;
                case 3:
                    chars = { topBeg: '┏', topEnd: '┓', botBeg: '┗', botEnd: '┛', char: '━', side: '┃' };
                    break;
                case 4:
                    chars = { topBeg: '╭', topEnd: '╮', botBeg: '╰', botEnd: '╯', char: '─', side: '│' };
                    break;
                case 5:
                    chars = { topBeg: '╭', topEnd: '╮', botBeg: '╰', botEnd: '╯', char: '═', side: '║' };
                    break;
                default:
                    chars = { topBeg: '┌', topEnd: '┐', botBeg: '└', botEnd: '┘', char: '─', side: '│' };
                    break;
            }

            // Calculate total width for each line including padding and border
            const totalWidth = originalLength + _paddingWidth + 2;

            // Ensure the width is not negative
            const paddedWidth = Math.max(totalWidth, 0);

            // Sides (add char to start/end of each line)
            input = input.split('\n').map(line =>
            {
                const lineWidth = line.length;

                if (lineWidth)
                {
                    // Ensure each line is padded correctly
                    const n = ((paddedWidth - lineWidth - 2) + 1)
                    const x = n && n > 0 ? n : 1;
                    return ansi.style(chars.side, { fg: color}) + ' ' + line + ' '.repeat(x) + ansi.style(chars.side, { fg: color});
                }

                else
                {
                    return ansi.style(chars.side, { fg: color}) + ' '.repeat(paddedWidth) + ansi.style(chars.side, { fg: color});
                }
            }).join('\n');

            // First line
            const firstLine = ansi.style(chars.topBeg + chars.char.repeat(paddedWidth) + chars.topEnd, { fg: color});
            input = firstLine + '\n' + input;

            // Last line
            const lastLine = ansi.style(chars.botBeg + chars.char.repeat(paddedWidth) + chars.botEnd, { fg: color});
            input = input + '\n' + lastLine;

            return input;
        },

        More :
        {
            __GetPaddingWidth
            : (options: types.formatOptions)
            : number =>
                {
                return (options.padding?.left || 0) + (options.padding?.right || 0);
            },
        }
    };

/* ---------------------------------------- ---- ----------------------------------------  */